import React, { useState, useCallback, useEffect, useRef } from 'react'
import ErrorBoundary from 'components/ErrorBoundary'
import Loading from 'components/Loading/Loading'
import Detail from './NetSuiteAssetDetail'
import StepNavigation from './components/StepNavigation'
import StepContent from './components/StepContent'
import ActionButtons from './components/ActionButtons'
import { useAssignFormState } from './hooks/useAssignFormState'
import { useStepValidation } from './hooks/useStepValidation'
import { useAssignment } from './hooks/useAssignment'
import { NetSuiteAsset } from 'types'

interface AssignProps {
  netSuiteAsset: NetSuiteAsset
  clearSelectedAsset: () => void
  defaultAdminEmail?: string
  onAssignmentSuccess?: () => void
}

const Assign: React.FC<AssignProps> = ({
  netSuiteAsset,
  clearSelectedAsset,
  defaultAdminEmail,
  onAssignmentSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const isMountedRef = useRef(true)

  const {
    activeTab,
    isStepValid,
    setIsStepValid,
    schoolData,
    email,
    confirmEmail,
    isConfirmed,
    handleStep,
    goBack,
    goNext,
  } = useAssignFormState(defaultAdminEmail)

  const { validateCurrentStep } = useStepValidation({
    activeTab,
    schoolData,
    email,
    confirmEmail,
    isConfirmed,
    setIsStepValid,
  })

  const { assignSchoolAndAdmin } = useAssignment({
    schoolData,
    email,
    netSuiteAsset,
    clearSelectedAsset,
    onAssignmentSuccess,
  })

  // Trigger validation when school data changes
  useEffect(() => {
    validateCurrentStep()
  }, [schoolData, validateCurrentStep])

  // Cleanup effect to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleStepWithValidation = useCallback(
    (data: any) => {
      handleStep(data)
    },
    [handleStep],
  )

  const handleGoBack = useCallback(() => {
    goBack()
    validateCurrentStep()
  }, [goBack, validateCurrentStep])

  const handleGoNext = useCallback(() => {
    goNext()
    validateCurrentStep()
  }, [goNext, validateCurrentStep])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()

      if (!isConfirmed) return

      setIsLoading(true)
      try {
        await assignSchoolAndAdmin()
      } catch {
        // Error handling is done in the hook
      } finally {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setIsLoading(false)
        }
      }
    },
    [isConfirmed, assignSchoolAndAdmin],
  )

  return (
    <ErrorBoundary>
      <Loading isLoading={isLoading} message="Saving..." />
      <Detail
        subscriptionExpirationDate={netSuiteAsset.expirationDate}
        productName={netSuiteAsset.product?.description || ''}
        subscriptionStartDate={netSuiteAsset.startDate}
      />
      <StepNavigation activeTab={activeTab} />
      <StepContent
        activeTab={activeTab}
        schoolData={schoolData}
        email={email}
        defaultAdminEmail={defaultAdminEmail}
        onStepChange={handleStepWithValidation}
      />
      <ActionButtons
        activeTab={activeTab}
        isStepValid={isStepValid}
        isConfirmed={isConfirmed}
        onCancel={clearSelectedAsset}
        onPrevious={handleGoBack}
        onNext={handleGoNext}
        onSubmit={handleSubmit}
      />
      <style>
        {`
          .lst-step {
            min-height: 500px;
          }
        `}
      </style>
    </ErrorBoundary>
  )
}

export default Assign
